import { TabContentWrapper } from '@/components/_common';
import { PageProps } from '@/types/PageProps';
import { Button, Descriptions, Divider, Skeleton, Table } from 'antd';
import { useRouteMatch } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { useDeliveryDetailsId } from '@/core/domain/stock/DeliveryMaterial/DeliveryMaterial.hooks';
import {
  DeliveryDetailLabels,
  DeliveryDetailType,
} from '@/core/domain/stock/DeliveryMaterial/DeliveryMaterial.types';
import moment from 'moment';
import useConfigurationColumnsPart from '@/hooks/useConfigurationColumnsPart';
import { quantityFormatter } from '@/utils/number';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import { useCompanyReduced } from '@/core/domain/enterprise/Companies/Companies.hooks';
import { useEffect } from 'react';

function DeliveryDetailsPage({ route }: PageProps) {
  const intl = useIntl().formatMessage;
  const { returnMaterialId } = useRouteMatch<{ returnMaterialId: string }>()
    .params;

  const deliveryDetails = useDeliveryDetailsId(returnMaterialId);
  const companyReduced = useCompanyReduced();

  useEffect(() => {
    if (deliveryDetails.data?.result?.companyId) {
      companyReduced.mutateAsync(deliveryDetails.data?.result?.companyId);
    }
  }, [deliveryDetails.data?.result?.companyId]);

  const columnsDelivery = [
    {
      dataIndex: 'complementIdentifier',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.os',
      }),
      ellipsis: true,
    },
    {
      dataIndex: 'description',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.description',
      }),
      ellipsis: true,
    },
    useConfigurationColumnsPart({}),
    {
      dataIndex: 'type',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.type',
      }),
      ellipsis: true,
      render: (value: DeliveryDetailType) => {
        return DeliveryDetailLabels[value];
      },
    },
    {
      dataIndex: 'requestQuantity',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.requestedQuantity',
      }),
      ellipsis: true,
      render: (value: string) => {
        return value ? quantityFormatter(+value) : '-';
      },
    },
    {
      dataIndex: 'deliveredQuantity',
      title: intl({
        id: 'deliveryOrReturnOfMaterials.deliverMaterial.modalDelivery.deliveryQuantity',
      }),
      ellipsis: true,
      render: (value: string) => {
        return value ? quantityFormatter(+value) : '-';
      },
    },
  ];

  function openReport() {
    window.open(
      `${window.location.origin}/privateReports/materialDelivery?deliveryId=${returnMaterialId}`,
      '_blank',
    );
  }

  return (
    <TabContentWrapper
      title={route.name}
      permissions={route.permissions}
      extra={
        <Button type="primary" onClick={openReport}>
          Relatório
        </Button>
      }
    >
      <Skeleton active loading={false}>
        <Descriptions
          column={{ xs: 1, sm: 2, md: 3, xl: 4 }}
          layout={'vertical'}
          className="workorder-descriptions"
          style={{ marginBottom: 24 }}
        >
          <Descriptions.Item label={'Data da entrega'}>
            {moment(deliveryDetails.data?.result?.deliveredDate).format(
              'DD/MM/YYYY',
            ) ?? '-'}
          </Descriptions.Item>

          <Descriptions.Item label={'Empresa'}>
            {companyReduced.data?.name ?? '-'}
          </Descriptions.Item>

          <Descriptions.Item label={'Entregue por'}>
            {deliveryDetails.data?.result?.deliveryManName ?? '-'}
          </Descriptions.Item>

          <Descriptions.Item label={'Recebido por'}>
            {deliveryDetails.data?.result?.receivedManName ?? '-'}
          </Descriptions.Item>
        </Descriptions>
      </Skeleton>

      <Table
        columns={columnsDelivery}
        dataSource={deliveryDetails.data?.result?.itemDeliveryDetail}
        loading={deliveryDetails.isLoading}
        rowKey={(row) => row.workOrderComplementId}
        pagination={false}
      />

      <Divider />

      <SpaceForButton>
        <ButtonBack path="/app/movements/deliveryOfMaterials" />
      </SpaceForButton>
    </TabContentWrapper>
  );
}

export default DeliveryDetailsPage;
